// src/components/profile/FollowButton.tsx

import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { followUser, unfollowUser } from "../../services/followService";
import "./FollowButton.css";

interface FollowButtonProps {
  targetUserId: number;
  initialIsFollowing: boolean;
  // Called after a successful follow/unfollow so the parent can update counts
  onToggle?: (nowFollowing: boolean) => void;
}

/**
 * Follow / Unfollow toggle button rendered on profile pages.
 * Optimistically updates UI and rolls back on error.
 */
const FollowButton: React.FC<FollowButtonProps> = ({
  targetUserId,
  initialIsFollowing,
  onToggle,
}) => {
  const { t } = useTranslation();
  const [isFollowing, setIsFollowing] = useState(initialIsFollowing);
  const [loading, setLoading] = useState(false);

  const handleClick = async () => {
    if (loading) return;
    setLoading(true);

    // Optimistic update
    const next = !isFollowing;
    setIsFollowing(next);
    onToggle?.(next);

    try {
      if (next) {
        await followUser(targetUserId);
      } else {
        await unfollowUser(targetUserId);
      }
    } catch {
      // Roll back on failure
      setIsFollowing(!next);
      onToggle?.(!next);
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      className={`follow-btn ${isFollowing ? "follow-btn--following" : "follow-btn--not-following"}`}
      onClick={handleClick}
      disabled={loading}
    >
      {isFollowing ? t("follow_unfollow") : t("follow_follow")}
    </button>
  );
};

export default FollowButton;